/**
 * Setting a tournament up — FR-TOUR-1, FR-TOUR-2 (admin-set draw), FR-TOUR-3.
 *
 * The one screen in this app where typing is fine. FR-ENTRY-2's ban on free
 * text is about the *session flow* — a court, twenty seconds between games,
 * one free hand. This is somebody at a table the night before, and the cost of
 * getting it wrong here is that ten people play the wrong fixtures all day.
 *
 * Even so, the draw itself is chosen, never typed: each place in the schedule
 * gets a picker over the club roster, so a tournament cannot create a player
 * by spelling one wrong, and the same person cannot be put in two places.
 *
 * The schedule is not editable here and that is deliberate. It arrives as a
 * named template that has been checked — 40 distinct match-ups, four games per
 * partnership, a rest every fifth round — and a form that let somebody retype
 * 40 fixtures on a phone would be a form for breaking those properties.
 */

import { useEffect, useState } from "react";
import { api, type Me, type Player } from "../api.js";

/** Mirrors `BALANCED_10_PLAYER_2_COURT`; the server builds the real fixtures. */
const TEMPLATE = {
  id: "balanced-10-2",
  teams: [
    { code: "A", defaultName: "Group A", codes: ["A1", "A2", "A3", "A4", "A5"] },
    { code: "B", defaultName: "Group B", codes: ["B1", "B2", "B3", "B4", "B5"] },
  ],
  games: 40,
  rounds: 20,
  courts: 2,
};

export function StartTournament({
  me,
  onCancel,
  onCreated,
}: {
  me: Me;
  onCancel: () => void;
  onCreated: (sessionId: string) => void;
}): React.JSX.Element {
  const [name, setName] = useState("Club tournament");
  const [date, setDate] = useState("");
  const [venue, setVenue] = useState("");
  const [target, setTarget] = useState(15);
  const [cap, setCap] = useState(17);
  const [teamNames, setTeamNames] = useState<Record<string, string>>(
    Object.fromEntries(TEMPLATE.teams.map((t) => [t.code, t.defaultName])),
  );
  /** Player code → player id. Empty until somebody is picked for that place. */
  const [draw, setDraw] = useState<Record<string, string>>({});
  const [roster, setRoster] = useState<Player[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .players()
      .then(setRoster)
      .catch(() => setError("Could not load the roster."));
    api
      .today()
      .then(({ defaults }) => {
        // The club's today in the club's timezone, not the phone's.
        setDate(defaults.date);
        setVenue(defaults.venue ?? "");
      })
      .catch(() => undefined);
  }, []);

  const taken = new Set(Object.values(draw).filter(Boolean));
  const allCodes = TEMPLATE.teams.flatMap((t) => t.codes);
  const unassigned = allCodes.filter((code) => !draw[code]);

  /**
   * BR-SCORE-1a — the caps a target can legally have.
   *
   * Offered as choices rather than a number field for the reason every other
   * value here is: a cap below the target or past double it is not a cap, and
   * a form that lets you type one only moves the rejection later.
   */
  const capOptions = [target + 1, target + 2, target + 3, Math.floor(1.5 * target - 1)]
    .filter((c, i, all) => c > target && c <= 2 * target && all.indexOf(c) === i)
    .sort((a, b) => a - b);

  return (
    <main className="session">
      <header className="session-head">
        <div className="brand">
          <h1 className="display">New tournament</h1>
          <div className="meta">{me.displayName}</div>
        </div>
        <button className="link" onClick={onCancel}>
          Back
        </button>
      </header>

      <div className="block stack">
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Club tournament" />
        </label>

        <label>
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>

        <label>
          Venue
          <input value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="SBH Expo" />
        </label>
      </div>

      <div className="block stack">
        <h3 className="card-head">Scoring</h3>

        <label>
          Games to
          <div className="row">
            {[15, 21, 11].map((n) => (
              <button
                key={n}
                type="button"
                className={n === target ? "chip chip-on" : "chip"}
                onClick={() => {
                  setTarget(n);
                  // A cap belonging to the old target would be silently wrong,
                  // and silently wrong is the failure this app exists to end.
                  setCap(n + 2);
                }}
              >
                {n}
              </button>
            ))}
          </div>
        </label>

        <label>
          Dead stop at
          <div className="row">
            {capOptions.map((n) => (
              <button
                key={n}
                type="button"
                className={n === cap ? "chip chip-on" : "chip"}
                onClick={() => setCap(n)}
              >
                {n}
              </button>
            ))}
          </div>
        </label>

        <p className="muted small">
          At {target} with a dead stop at {cap}, a game goes to {target} and then
          needs two clear — but ends the moment somebody reaches {cap}, however
          the score stands. So {cap}–{cap - 1} is a win and nothing goes past it.
          One point per win; a tie is decided on score difference.
        </p>
      </div>

      <div className="block">
        <h3 className="card-head">The draw</h3>
        <p className="muted small">
          {TEMPLATE.games} games over {TEMPLATE.rounds} rounds on {TEMPLATE.courts} courts.
          Everybody partners each team-mate four times and rests every fifth round.
        </p>

        {TEMPLATE.teams.map((team) => (
          <div key={team.code} className="drawteam">
            <label>
              Team {team.code}
              <input
                value={teamNames[team.code]}
                onChange={(e) => setTeamNames({ ...teamNames, [team.code]: e.target.value })}
              />
            </label>

            {team.codes.map((code) => (
              <label key={code} className="drawslot">
                {code}
                <select
                  value={draw[code] ?? ""}
                  onChange={(e) => setDraw({ ...draw, [code]: e.target.value })}
                >
                  <option value="">— pick a player —</option>
                  {(roster ?? [])
                    .filter((p) => p.id === draw[code] || !taken.has(p.id))
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.displayName}
                      </option>
                    ))}
                </select>
              </label>
            ))}
          </div>
        ))}
      </div>

      {error ? <p className="error">{error}</p> : null}

      {unassigned.length > 0 ? (
        <p className="muted small">
          Still to fill: <b>{unassigned.join(", ")}</b>
        </p>
      ) : null}

      <button
        className="primary"
        disabled={busy || unassigned.length > 0 || name.trim() === ""}
        onClick={() => {
          setBusy(true);
          setError(null);
          api
            .createTournament({
              name: name.trim(),
              scheduleTemplateId: TEMPLATE.id,
              targetScore: target,
              capScore: cap,
              pointsPerWin: 1,
              date: date || undefined,
              venue: venue.trim() || null,
              teams: TEMPLATE.teams.map((team) => ({
                code: team.code,
                name: teamNames[team.code].trim() || team.defaultName,
                members: team.codes.map((code) => ({ code, playerId: draw[code] })),
              })),
            })
            .then((created) => onCreated(created.sessionId))
            .catch((e: unknown) =>
              setError(e instanceof Error ? e.message : "Could not create the tournament."),
            )
            .finally(() => setBusy(false));
        }}
      >
        Create tournament
      </button>
    </main>
  );
}
